import * as Castle from '@castleio/castle-js';
import {
    type DataTag,
    hashKey,
    type Query,
    QueryClient,
    type QueryKey,
    QueryObserver,
    queryOptions,
    type SetDataOptions,
    type SkipToken,
    skipToken,
    type UseQueryResult,
    type UseSuspenseQueryOptions,
    useQueries,
    useQuery,
    useSuspenseQuery,
} from '@tanstack/react-query';

export { hashKey as hashQueryKey } from '@tanstack/react-query';

import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client';
import { atom, useAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import * as React from 'react';
import * as z from 'zod';

import * as api from '@gitbook/api-client';
import { getAPIEndpoint } from '@gitbook/env';
import { HTTPError, Logger, Trace } from '@gitbook/util-debug';
import {
    useEventCallback,
    useLiveRef,
    usePrevious,
    useStable,
    useStateWithDeps,
} from '@gitbook/util-react-hooks';
import { filterOutNullable, type MaybePromise } from '@gitbook/util-ts';

import { AuthTokenUnavailableError, getRetriedIdTokenForAPIRequest } from '../auth';
import { onChannelsReconnected, subscribeToRealtimeChannelId } from '../channels';
import { createReactQueryIDBPersister } from './indexedDB';

const logger = Logger('api');

/**
 * Test modes for the API.
 * - **slow**: Delays all queries and mutations. The default delay is 1 second.
 * - **slow-query**: Delays all queries. The default delay is 1 second.
 * - **slow-mutation**: Delays all mutations. The default delay is 1 second.
 * - **selective**: Only critical API endpoints are accessible; non-critical ones return a 500 error.
 */
const TestModeSchema = z.enum(['slow', 'slow-query', 'slow-mutation', 'selective']);

/**
 * Parse test modes from a list of strings.
 */
function parseTestModes(input: string[]) {
    return input
        .map((input) => {
            const result = TestModeSchema.safeParse(input);
            if (result.success) {
                return result.data;
            }
            return null;
        })
        .filter(filterOutNullable);
}

/**
 * Returns the test modes from the URL or environment variable.
 */
function getTestModes() {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('apiTestMode')) {
        return parseTestModes(searchParams.getAll('apiTestMode'));
    }
    if (process.env.GITBOOK_API_TEST_MODE) {
        return parseTestModes(process.env.GITBOOK_API_TEST_MODE.split(','));
    }
    return [];
}

/**
 * Returns the slow duration from the URL or environment variable.
 */
function getTestModeDelay() {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('apiDelay')) {
        return Number.parseInt(searchParams.get('apiDelay') ?? '', 10);
    }
    if (process.env.GITBOOK_API_TEST_DELAY) {
        return Number.parseInt(process.env.GITBOOK_API_TEST_DELAY, 10);
    }
    return 1000;
}

const testModes = getTestModes();
const testModeDelay = getTestModeDelay();

/**
 * Delay a number of milliseconds.
 */
async function delay(ms: number) {
    await new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Delay the response if the test mode is enabled.
 */
async function testDelayResponse(params: api.FullRequestParams) {
    if (testModes.includes('slow')) {
        await delay(testModeDelay);
    } else if (testModes.includes('slow-query') && params.method === 'GET') {
        await delay(testModeDelay);
    } else if (testModes.includes('slow-mutation') && params.method !== 'GET') {
        await delay(testModeDelay);
    }
}

// Server side events

/**
 * Server-sent event subscription.
 */
type APINotificationsSubscription = {
    unsubscribe: () => void;
};

/**
 * Server-sent event listener.
 */
type APINotificationsListener = (executionId: string | null) => void;

// ---

export interface APIResourceInput<Id extends api.OperationId = api.OperationId> {
    /**
     * The operation ID to call.
     */
    operationId: Id;
    /**
     * The parameters to pass to the operation.
     */
    params?: api.OperationSpec<Id>['RequestParams'];
    /**
     * The query parameters to pass to the operation.
     */
    query?: api.OperationSpec<Id>['RequestQuery'];
    /** Request body for cacheable reads; never include credentials. */
    body?: api.OperationSpec<Id>['RequestBody'];
    /**
     * Whether to return null on error.
     */
    nullOnError?: boolean;
    /**
     * Whether mutations on the query's channels wait for its refetch before resolving.
     * @default true
     */
    awaitOnMutation?: boolean;
}

export const apiClient = new api.GitBookAPI({
    getAuthToken: getRetriedIdTokenForAPIRequest,
    refreshAuthToken: () => getRetriedIdTokenForAPIRequest({ forceRefresh: true }),
    getCastleToken: async () => Castle.createRequestToken(),
    endpoint: getAPIEndpoint({
        // On the application frontend, we use the API endpoint served on the same domain as the application frontend to reduce CORS roundtrips.
        useAppDomain: true,
    }),
    criticalityLevel: testModes.includes('selective') ? 'high' : undefined,
    onResponse: async (params, response) => {
        // Mark the content as stale if we cache it from result of a list or a mutation.
        // So it can subscribe to channels and invalidate the cache when a mutation is done.
        const cacheOptions = { updatedAt: Date.now() - STALE_TIME };

        // Cache list items if the response is a list
        cacheAPIListItems(response.data, cacheOptions);

        // Cache the resource if the response is a single resource
        // This is needed to:
        // - Cache mutation responses (if a single resource is returned)
        // - Cache get response for both nullOnError and non-nullOnError cases
        cacheAPIResource(response.data, cacheOptions);

        // Ignore errors from invalidation
        const invalidations = invalidateMutationDependencies(response);

        await testDelayResponse(params);

        // Wait for invalidations to be done. It means all queries are refetched during the mutation.
        await invalidations;
    },
});

/**
 * Schema for a located API resource containing urls: { location: string }.
 */
const LocatedSchema = z.object({
    urls: z.object({
        location: z.string(),
    }),
});

/**
 * Check if the data is a located API resource.
 */
function checkIsLocated(data: unknown): data is { urls: { location: string } } {
    return LocatedSchema.validate(data);
}

/**
 * Schema for a list of located API resources.
 */
const LocatedListSchema = z.object({
    items: z.array(LocatedSchema),
});

/**
 * Check if the data is a list of located API resources.
 */
function checkIsLocatedList(data: unknown): data is { items: { urls: { location: string } }[] } {
    return LocatedListSchema.validate(data);
}

/**
 * Invalidate or update the cache of mutation dependencies based on channels.
 */
async function invalidateMutationDependencies(response: api.HttpResponse<unknown>) {
    const { headers } = response;
    const executionId = api.getExecutionIdForHeaders(headers);
    const channels = api.getMutatedChannelsForHeaders(headers);
    const location = checkIsLocated(response.data) ? response.data.urls.location : null;
    await Promise.all(
        channels.map(async (channelId) => {
            const cacheKeys = queryKeyByChannel.get(channelId);
            if (!cacheKeys) {
                return;
            }
            // If the response of a mutation is 205 (Reset Content), we invalidate all the cache keys
            if (response.status === 205) {
                await Promise.all(
                    Array.from(cacheKeys).map(async (key) => {
                        await invalidateQueriesFromMutationResponse(key, executionId);
                    })
                );
                return;
            }

            await Promise.all(
                Array.from(cacheKeys).map(async (key) => {
                    // If the response of mutation has a location we will update the cache
                    if (location) {
                        const data = apiQueryClient.getQueryData(key);
                        // If the data is a list, we will update the items that have the same location
                        if (checkIsLocatedList(data)) {
                            const hasMutationItem = data.items.some(
                                (item) => item.urls.location === location
                            );
                            // If an item is related to mutation we update it
                            if (hasMutationItem) {
                                apiQueryClient.setQueryData(key, {
                                    ...data,
                                    items: data.items.map((item) =>
                                        item.urls.location === location ? response.data : item
                                    ),
                                });
                                markExecutionIdHandled(key, executionId);
                                return;
                            }
                        }
                        // If the data is a single item, we will update it
                        if (checkIsLocated(data)) {
                            if (data.urls.location === location) {
                                apiQueryClient.setQueryData(key, response.data);
                                markExecutionIdHandled(key, executionId);
                                return;
                            }
                        }
                    }

                    // If we don't find a way to update the cache, we invalidate the query
                    await invalidateQueriesFromMutationResponse(key, executionId);
                })
            );
        })
    );
}

/**
 * Cache the items from a LocatedList in the query client.
 */
function cacheAPIListItems(data: unknown, options?: SetDataOptions) {
    if (checkIsLocatedList(data)) {
        data.items.forEach((item) => cacheAPIResource(item, options));
    }
}

/**
 * Cache an API resource in the query client.
 */
function cacheAPIResource(data: unknown, options?: SetDataOptions) {
    if (checkIsLocated(data)) {
        const queryKey = getAPIQueryKeyFromLocation(data.urls.location);
        logger.info(`pre-caching "${data.urls.location}" "${queryKey.join(', ')}"`, {
            data,
            options,
        });

        apiQueryClient.setQueryData(queryKey, data, options);
        apiQueryClient.setQueryData([...queryKey, { nullOnError: true }], data, options);
    }
}

/**
 * Read the value of an API resource from the suspense cache.
 */
export function readAPICache<TResource extends APIResourceInput>(
    input: TResource,
    options?: FetchAPICacheOptions
): APIQueryFnData<TResource['operationId'], TResource['nullOnError']> {
    const data = fetchAPICache(input, options);
    if (data instanceof Promise) {
        throw data;
    }

    return data;
}

type FetchAPICacheOptions = {
    /**
     * If cached data is stale, return it immediately and trigger a background revalidation.
     */
    staleWhileRevalidate?: boolean;
};

/**
 * Fetch the value of an API resource from the suspense cache.
 */
export function fetchAPICache<TResource extends APIResourceInput>(
    input: TResource,
    options?: FetchAPICacheOptions
): MaybePromise<APIQueryFnData<TResource['operationId'], TResource['nullOnError']>> {
    const queryOptions = apiQuery(input);
    const data = apiQueryClient.getQueryData(queryOptions.queryKey);

    // Reference the query key in the channels to invalidate the cache when a mutation is done.
    const reference = (
        data: APIQueryFnData<TResource['operationId'], TResource['nullOnError']>
    ) => {
        referenceQueryKeyInChannels(queryOptions.queryKey);
        return data;
    };

    // If we don't have data, we will fetch it.
    if (data === undefined) {
        return apiQueryClient.ensureQueryData(queryOptions).then(reference);
    }

    // If we have data, we will check if the query is stale.
    const query = apiQueryClient.getQueryCache().build(apiQueryClient, queryOptions);
    const isStale = query.isStaleByTime(STALE_TIME);

    // If the query is stale, we prefetch it and then ensure the data.
    if (isStale) {
        const prefetch = apiQueryClient.prefetchQuery(queryOptions);
        if (options?.staleWhileRevalidate) {
            void prefetch.catch(() => {
                // Ignore error here
            });
            return reference(data);
        }
        return prefetch.then(() => apiQueryClient.ensureQueryData(queryOptions)).then(reference);
    }

    // If we have data not stale, we return it.
    return reference(data);
}

/**
 * Listen to an API query.
 */
export function liveAPIQuery<TOperationId extends api.OperationId, TNullable>(
    input: APIResourceInput<TOperationId> & { nullOnError?: TNullable },
    callback: (data: APIQueryFnData<TOperationId, TNullable>) => void
): () => void {
    let cancelled = false;
    let cancelSubscribe: (() => void) | undefined;

    (async () => {
        // First fetch the data to ensure the query is initialized in the cache
        const data = await fetchAPICache(input);
        if (cancelled) {
            return;
        }
        callback(data);

        // After initial data, we subscribe to the query.
        const observer = new QueryObserver(apiQueryClient, apiQuery(input));
        cancelSubscribe = observer.subscribe((entry) => {
            if (entry.data && !cancelled) {
                callback(entry.data as APIQueryFnData<TOperationId, TNullable>);
            }
        });
    })();

    return () => {
        cancelled = true;
        cancelSubscribe?.();
    };
}

/**
 * Keep track of the pagination state for a resource.
 * The key is the hash of the resource key.
 */
function usePaginationState(key: string) {
    return useStateWithDeps<{
        page: string | null;
        pageIndex: number;
        previousPages: Record<string, string | null>;
    }>(
        {
            page: null,
            pageIndex: 0,
            previousPages: {},
        },
        [key],
        { resetOnDepsChange: true }
    );
}

/**
 * React hook to provide some controls to navigate through paginated API resources.
 */
function usePaginationControls(input: {
    page: string | null;
    nextPage: string | null;
    previousPage: string | null | undefined;
    setPage: (page: string | null, increment: number) => Promise<void>;
}): {
    next: (() => Promise<void>) | undefined;
    previous: (() => Promise<void>) | undefined;
} {
    const { nextPage, previousPage, page, setPage } = input;
    const liveRef = useLiveRef({ page, setPage });

    const next = React.useMemo(() => {
        if (!nextPage) {
            return undefined;
        }

        return async () => {
            const { setPage } = liveRef.current;
            return setPage(nextPage, 1);
        };
    }, [nextPage, liveRef]);

    const previous = React.useMemo(() => {
        if (previousPage === undefined) {
            return undefined;
        }

        return async () => {
            const { setPage } = liveRef.current;
            return setPage(previousPage, -1);
        };
    }, [previousPage, liveRef]);

    return { next, previous };
}

type APIResourceParams<Id extends api.OperationId> = api.OperationSpec<Id>['RequestParams'] &
    api.OperationSpec<Id>['RequestQuery'] & { body?: api.OperationSpec<Id>['RequestBody'] };

type APIResourceOptions<TNullOnError extends boolean> = {
    /**
     * Whether to return null on error 4xx.
     * @deprecated Use `APIErrorBoundary` instead.
     */
    nullOnError?: TNullOnError;

    /** @see TanStack refetchOnMount */
    refetchOnMount?: boolean | 'always' | ((query: Query) => boolean | 'always');

    /**
     * Whether mutations on the query's channels wait for its refetch before resolving. Opt out
     * when the query is not what the mutation's caller needs fresh, so it refetches in the
     * background instead.
     * @default true
     */
    awaitOnMutation?: boolean;
};

/**
 * Utility to create an API resource input with strict typing.
 * It should be used to create the input for the `useAPI` hook and avoid the `as const` cast.
 */
export function apiResource<Id extends api.OperationId, TNullOnError extends boolean = false>(
    operationId: Id,
    params?: APIResourceParams<Id>,
    options?: APIResourceOptions<TNullOnError>
) {
    const { body, ...requestParams } = params ?? {};
    const { queryParams, pathParams } = api.splitAPIOperationParams({
        operationId,
        params: requestParams,
    });
    return {
        nullOnError: options?.nullOnError ?? false,
        refetchOnMount: options?.refetchOnMount ?? undefined,
        awaitOnMutation: options?.awaitOnMutation,
        query: queryParams,
        body,
        operationId,
        params: pathParams,
    } as APIResourceInput<Id> & {
        nullOnError: TNullOnError extends true ? true : false;
        refetchOnMount: APIResourceOptions<TNullOnError>['refetchOnMount'];
    };
}

// When using Suspense, the component is mounted several times
// We want to avoid multiple queries to be triggered caused of the "fetch on mount" behavior
// See https://github.com/TanStack/query/discussions/5975#discussioncomment-6938037
// Also 15s seems a good value to avoid too much refetching
const STALE_TIME = 15_000;

// We cache queries for 24 hours
// During this time, the cached data will be used first, while the query is refetched in the background
const CACHE_TIME = 1000 * 60 * 60 * 24;

/**
 * Query client used in React Query.
 */
export const apiQueryClient = new QueryClient({
    defaultOptions: {
        queries: {
            gcTime: CACHE_TIME,
            staleTime: STALE_TIME,
        },
    },
});

/**
 * To improve load time, we persist API queries to the browser IndexedDB.
 */
export const apiQueryPersister = createReactQueryIDBPersister({
    key: 'api',
    staleTime: STALE_TIME,
});

/**
 * Options for persistence.
 */
export const apiQueryPersistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
    persister: apiQueryPersister,
    maxAge: CACHE_TIME,

    // Only persist successful API queries. UI state that happens to use React Query for its
    // asynchronous storage should remain in its own storage backend.
    dehydrateOptions: {
        shouldDehydrateQuery: (query) =>
            query.state.status === 'success' && query.meta?.persist !== false,
    },

    // We bust the cache each time the API specification is changing as it implies the shape of data could be different
    // We don't want to bust based on the app version as we deploy more often than we change our API spec.
    buster: testModes.length > 0 ? String(Math.random()) : api.specVersionHash,
};

// Listen to query cache events to track active queries
apiQueryClient.getQueryCache().subscribe((event) => {
    switch (event.type) {
        case 'observerResultsUpdated':
        case 'observerAdded': {
            if (event.query.observers.length > 0) {
                onQueryKeyIsActive(event.query.queryKey);
            }
            return;
        }
        case 'observerRemoved': {
            if (event.query.observers.length === 0) {
                onQueryKeyIsInactive(event.query.queryKey);
            }
            return;
        }
    }
});

/**
 * Represent an API query key.
 * - route: The route of the query.
 * - options: Query parameters and nullOnError option.
 */
type APIQueryKey =
    | [string]
    | [string, Pick<APIResourceInput<any>, 'query' | 'body' | 'nullOnError'>];

type Interpolator = (params: Record<string, string>) => string;

function compileRoute(route: string): Interpolator {
    const parts = route.split(/\{([^}]+)\}/g);
    return (params) =>
        parts.map((part, i) => (i % 2 ? (params[part] ?? `{${part}}`) : part)).join('');
}

const routeCache = new Map<string, Interpolator>();

/**
 * Interpolate a route with parameters to generate a location.
 */
function interpolateRoute(route: string, params: Record<string, string>): string {
    let interpolate = routeCache.get(route);
    if (!interpolate) {
        interpolate = compileRoute(route);
        routeCache.set(route, interpolate);
    }
    return interpolate(params);
}

/**
 * Returns API query key from a location.
 */
function getAPIQueryKeyFromLocation(location: string): [string] {
    return [location];
}

/**
 * Returns the query key for an API query.
 */
export function getAPIQueryKey(input: APIResourceInput): APIQueryKey {
    const apiOperation = api.operations[input.operationId];
    const location = interpolateRoute(apiOperation.route, input.params ?? {});
    const queryKey = getAPIQueryKeyFromLocation(location);
    const options: Pick<APIResourceInput<any>, 'query' | 'body' | 'nullOnError'> = {};
    // `splitAPIOperationParams` always returns a query object, so an operation taking only path
    // params yields `{}`. Keying on it would make every such query unreachable from the
    // location-only keys written by `cacheAPIResource`, defeating the mutation pre-cache.
    if (input.query && Object.keys(input.query).length) {
        options.query = input.query;
    }
    if (input.body !== undefined) {
        options.body = input.body;
    }
    if (input.nullOnError) {
        options.nullOnError = input.nullOnError;
    }
    if (Object.keys(options).length) {
        return [...queryKey, options];
    }
    return queryKey;
}

/**
 * Skip a query by returning a token that will be used to skip the query.
 */
function skipQuery(uniqueKey: string) {
    return queryOptions<null, HTTPError>({
        queryKey: ['$skip', uniqueKey],
        // skipToken is not allowed in Suspense queries, so we just do it ourself.
        queryFn: () => null,
        initialData: null,
    }) as UseSuspenseQueryOptions;
}

/**
 * Returns the subscription channels for a query key.
 */
function getSubscriptionChannelsForQueryKey(queryKey: APIQueryKey) {
    const headers = getQueryHeaders(queryKey);
    if (!headers) {
        return null;
    }
    return api.getSubscriptionChannelsForHeaders(headers);
}

/**
 * Track the subscriptions by query key hash.
 */
const querySSESubscriptions = new Map<
    string,
    { queryKey: APIQueryKey; subscriptions: Set<APINotificationsSubscription> }
>();

// While a channel is down its mutation messages are dropped, and only the last one is
// replayed on reconnection, so a query bound to it can have missed an invalidation.
onChannelsReconnected(() => {
    querySSESubscriptions.forEach(({ queryKey }) => {
        // Every tracked query is active by construction: they are subscribed when
        // observed and unsubscribed when the last observer goes away.
        // We cannot just resend every single query in the app, we just mark them as stale
        void apiQueryClient.invalidateQueries({ queryKey, exact: true, refetchType: 'none' });
    });
});

/**
 * Subscribe to server-side events on a query.
 */
function subscribeQueryToServerSentEvents(queryKey: APIQueryKey) {
    const hash = hashKey(queryKey);
    if (querySSESubscriptions.has(hash)) {
        return;
    }

    // Get URLS temporarily associated with the query key
    const channels = getSubscriptionChannelsForQueryKey(queryKey);

    // Some queries may not have subscription urls
    const subscriptions = new Set<APINotificationsSubscription>();
    const listener: APINotificationsListener = (executionId) => {
        invalidateQueriesFromMutation(queryKey, executionId);
    };

    channels?.forEach((channelId) => {
        const unsubscribe = subscribeToRealtimeChannelId(channelId, (data) => {
            listener(data && 'executionId' in data ? data.executionId : null);
        });
        subscriptions.add({ unsubscribe });
    });

    if (subscriptions.size) {
        querySSESubscriptions.set(hash, { queryKey, subscriptions });
    }
}

/**
 * Unsubscribe from server-side events on a query.
 */
function unsubscribeQueryToServerSentEvents(queryKey: APIQueryKey) {
    const hash = hashKey(queryKey);
    const entry = querySSESubscriptions.get(hash);
    if (!entry) {
        return;
    }
    entry.subscriptions.forEach((subscription) => {
        subscription.unsubscribe();
    });
    querySSESubscriptions.delete(hash);
}

/**
 * Track the executionIds by query key hash.
 */
const executionIdsByQueryKeyHash = new Map<string, Map<string, Promise<void>>>();

/**
 * Enable executionId tracking for a query key.
 * It means that the query key will be invalidated only once per executionId.
 */
function enableExecutionIdTracking(queryKey: APIQueryKey) {
    const hash = hashKey(queryKey);
    if (!executionIdsByQueryKeyHash.has(hash)) {
        executionIdsByQueryKeyHash.set(hash, new Map());
    }
}

/**
 * Disable executionId tracking for a query key.
 * It means that the query key will be invalidated every time.
 * We disable it when the query is not observed anymore to avoid memory leaks.
 */
function disableExecutionIdTracking(queryKey: APIQueryKey) {
    const hash = hashKey(queryKey);
    executionIdsByQueryKeyHash.delete(hash);
}

/**
 * Mark an executionId as already handled for a query key, so a later self-notification SSE event
 * for the same mutation does not trigger a redundant refetch.
 */
function markExecutionIdHandled(queryKey: APIQueryKey, executionId: string | null): void {
    if (!executionId) {
        return;
    }
    const hash = hashKey(queryKey);
    executionIdsByQueryKeyHash.get(hash)?.set(executionId, Promise.resolve());
}

/**
 * Invalidate a query from a mutation with an executionId.
 */
async function invalidateQueriesFromMutation(queryKey: APIQueryKey, executionId: string | null) {
    // Active queries have the executionIds tracked
    // to avoid multiple invalidations for the same executionId
    // - One emitted from the result of the mutation
    // - Another emitted from the server-sent event
    // - If the query is active, we invalidate it
    if (executionId) {
        const hash = hashKey(queryKey);
        const executionsMap = executionIdsByQueryKeyHash.get(hash);
        if (executionsMap) {
            const existing = executionsMap.get(executionId);
            // If already invalidated, we skip
            if (existing) {
                return existing;
            }
            const promise = apiQueryClient.invalidateQueries({
                queryKey,
                exact: true,
            });
            executionsMap.set(executionId, promise);
            return promise;
        }
    }

    // If the query is not active, we remove it, so when the page is loaded,
    // it will be refetched
    return apiQueryClient.removeQueries({
        queryKey,
        exact: true,
    });
}

/**
 * Invalidate a query from the response of a mutation, holding the mutation until the query has
 * refetched unless it opted out with `awaitOnMutation`.
 */
async function invalidateQueriesFromMutationResponse(
    queryKey: APIQueryKey,
    executionId: string | null
) {
    const query = apiQueryClient.getQueryCache().find({ queryKey, exact: true });
    if (query?.meta?.awaitOnMutation === false) {
        void invalidateQueriesFromMutation(queryKey, executionId);
        return;
    }

    await invalidateQueriesFromMutation(queryKey, executionId);
}

/**
 * Keep track of the query keys by channel to invalidate them when a mutation is done.
 */
const queryKeyByChannel = new Map<string, Set<APIQueryKey>>();

/**
 * Reference the query key in the channels to invalidate the cache when a mutation is done.
 */
function referenceQueryKeyInChannels(queryKey: APIQueryKey) {
    const headers = getQueryHeaders(queryKey);
    if (!headers) {
        return;
    }
    const channels = api.getSubscriptionChannelsForHeaders(headers);
    channels.forEach((channelId) => {
        const cacheKeys = queryKeyByChannel.get(channelId) ?? new Set();
        cacheKeys.add(queryKey);
        queryKeyByChannel.set(channelId, cacheKeys);
    });
}

/**
 * Triggered when a query is active (observed).
 */
function onQueryKeyIsActive(queryKey: APIQueryKey) {
    enableExecutionIdTracking(queryKey);
    referenceQueryKeyInChannels(queryKey);
    subscribeQueryToServerSentEvents(queryKey);
}

/**
 * Triggered when a query becomes inactive (non-observed anymore).
 */
function onQueryKeyIsInactive(queryKey: APIQueryKey) {
    unsubscribeQueryToServerSentEvents(queryKey);
    disableExecutionIdTracking(queryKey);
}

/**
 * Returns the data of an API query function.
 */
type APIQueryFnData<TOperationId extends api.OperationId, TNullable> = TNullable extends true
    ? api.OperationSpec<TOperationId>['ResponseBody'] | null
    : api.OperationSpec<TOperationId>['ResponseBody'];

/**
 * Returns of apiQuery function.
 */
type APIQueryOptions<TOperationId extends api.OperationId, TNullable> = UseSuspenseQueryOptions<
    APIQueryFnData<TOperationId, TNullable>,
    HTTPError,
    APIQueryFnData<TOperationId, TNullable>,
    APIQueryKey
> & {
    queryKey: DataTag<APIQueryKey, APIQueryFnData<TOperationId, TNullable>>;
};

/**
 * Compute an headers query key from an API query key.
 * There is no real query associated with this key, it's just a way to store the headers.
 */
function getHeadersQueryKey(
    queryKey: APIQueryKey
): DataTag<QueryKey, api.APIResponseObjectHeaders> {
    return ['$$headers$$', ...queryKey] as unknown as DataTag<
        QueryKey,
        api.APIResponseObjectHeaders
    >;
}

/**
 * Store the headers of a query in the cache.
 */
function storeQueryHeaders(queryKey: APIQueryKey, headers: api.APIResponseHeaders): void {
    const headersQueryKey = getHeadersQueryKey(queryKey);
    const serializableHeaders =
        headers instanceof Headers ? Object.fromEntries(headers.entries()) : headers;
    apiQueryClient.setQueryData(headersQueryKey, serializableHeaders);
}

/**
 * Get the headers of a query from the cache.
 */
function getQueryHeaders(queryKey: APIQueryKey): api.APIResponseObjectHeaders | undefined {
    const headersQueryKey = getHeadersQueryKey(queryKey);
    return apiQueryClient.getQueryData(headersQueryKey);
}

/**
 * Create query options for a query that fetches an API resource.
 * It handles channel subscriptions to invalidate the cache when a mutation is done.
 */
export function apiQuery<TResource extends APIResourceInput>(
    operationId: TResource
): APIQueryOptions<TResource['operationId'], TResource['nullOnError']>;
export function apiQuery<
    TOperationId extends api.OperationId,
    TNullOnError extends boolean = false,
>(
    operationId: TOperationId,
    params: APIResourceParams<TOperationId>,
    options?: APIResourceOptions<TNullOnError>
): APIQueryOptions<TOperationId, TNullOnError extends true ? true : false>;
export function apiQuery<TOperationId extends api.OperationId>(
    operationId: TOperationId,
    params: APIResourceParams<TOperationId> | SkipToken,
    options?: APIResourceOptions<boolean>
): APIQueryOptions<TOperationId, true>;
export function apiQuery<
    TOperationId extends api.OperationId,
    TResource extends APIResourceInput<TOperationId>,
    TNullOnError extends boolean,
>(
    operationId: TOperationId | TResource,
    params?: APIResourceParams<TOperationId> | SkipToken,
    options?: APIResourceOptions<TNullOnError>
) {
    let input: APIResourceInput<TOperationId>;

    // Handle the case where the operationId is a string
    if (typeof operationId === 'string') {
        if (params === skipToken) {
            return skipQuery(operationId);
        }
        input = apiResource(operationId, params, options);
    }
    // Else it means the operationId is an apiResource object
    else {
        input = operationId;
    }

    const queryKey = getAPIQueryKey(input);

    return queryOptions<api.OperationSpec<TOperationId>['ResponseBody'], HTTPError>({
        queryKey,
        meta: input.awaitOnMutation === false ? { awaitOnMutation: false } : undefined,
        retry: testModes.includes('selective')
            ? 0
            : (failureCount, error) => {
                  if (error instanceof AuthTokenUnavailableError) {
                      return false;
                  }

                  // Retry on 5xx errors or when the error is not an HTTP error
                  if (!error.statusCode || error.statusCode >= 500) {
                      return failureCount < 3;
                  }
                  return false;
              },
        queryFn: async (ctx) => {
            const apiOperation = api.operations[input.operationId];
            const path = api.getAPIOperationPath(input);
            logger.info(`fetching query "${input.operationId}": "${apiOperation.method} ${path}"`, {
                query: input.query,
            });
            const trace = new Trace('apiQuery.queryFn', {
                sampleRate: 0,
            });
            trace.label('operation', input.operationId);
            return trace.wrap(async () => {
                try {
                    const response = await apiClient.request({
                        path,
                        method: apiOperation.method,
                        secure: true,
                        query: input.query,
                        body: input.body,
                        type: input.body === undefined ? undefined : api.ContentType.Json,
                        format: 'json',
                        signal: ctx.signal,
                    });

                    storeQueryHeaders(queryKey, response.headers);

                    trace.label('data', response.data ? 'valid' : 'none');

                    return response.data;
                } catch (error: unknown) {
                    if (
                        error instanceof HTTPError &&
                        // On 5xx errors, we throw and we don't subscribe to anything.
                        Number(error.statusCode) < 500
                    ) {
                        storeQueryHeaders(queryKey, error.headers);

                        if (input.nullOnError) {
                            trace.label('error', error.message);
                            trace.label('data', null);
                            return null;
                        }
                    }
                    if (error instanceof AuthTokenUnavailableError && input.nullOnError) {
                        trace.label('error', error.message);
                        trace.label('data', null);
                        return null;
                    }
                    throw error;
                }
            });
        },
    });
}

function usePaginatedAPIQueryBase<
    TResource extends APIResourceInput,
    TSuspense extends boolean = false,
    TSkip extends boolean = false,
>(
    resource: TResource,
    options?: {
        /** Suspense the first query. */
        suspense?: TSuspense;

        /** Skip the query. */
        skip?: TSkip;

        /**
         * Suspense when parameters change.
         * @default false
         */
        suspenseOnParamsChange?: boolean;
    }
) {
    const suspenseOnParamsChange = options?.suspenseOnParamsChange ?? false;
    // Compute the resource key and hash to link the pagination state to the resource
    const resourceKey = getAPIQueryKey(resource);
    const resourceHash = hashKey(resourceKey);

    const [state, setState] = usePaginationState(resourceHash);

    const paginatedQueryParams = (page: string | null) => {
        return resource.query || page
            ? {
                  ...resource?.query,
                  ...(page ? { page } : {}),
              }
            : undefined;
    };

    const paginatedApiQuery = (page: string | null) => {
        return apiQuery({ ...resource, query: paginatedQueryParams(page) });
    };

    const nonPaginatedQuery = apiQuery(resource);
    const previousNonPaginatedQuery = usePrevious(nonPaginatedQuery);
    const paginatedQuery = paginatedApiQuery(state.page);
    const query = options?.skip ? skipQuery('paginated') : paginatedQuery;
    const [initialQueryKey] = React.useState(paginatedQuery.queryKey);

    // If suspenseOnParamsChange is false, we suspend only the initial query
    // else we suspend when the resource key changes.
    const shouldSuspense =
        Boolean(options?.suspense) &&
        (suspenseOnParamsChange
            ? nonPaginatedQuery !== previousNonPaginatedQuery
            : hashKey(initialQueryKey) === hashKey(query.queryKey));

    const { page, previousPages } = state;
    type Data = api.OperationSpec<TResource['operationId']>['ResponseBody'] & api.List;
    const previousDataRef = React.useRef<Data | undefined>(undefined);
    const suspenseResult = useSuspenseQuery(shouldSuspense ? query : skipQuery('paginated'));
    const queryResult = useQuery(
        shouldSuspense
            ? skipQuery('paginated')
            : {
                  ...query,
                  placeholderData: () => previousDataRef.current as any,
              }
    );

    const result = shouldSuspense ? suspenseResult : queryResult;

    React.useEffect(() => {
        if (result.data) {
            previousDataRef.current = result.data;
        }
    }, [result.data]);

    // Throw errors
    if (result.error) {
        throw result.error;
    }

    const data = result.data as TSkip extends true
        ? null
        : TSuspense extends true
          ? Data
          : Data | undefined;

    const controls = usePaginationControls({
        nextPage: data?.next?.page ?? null,
        previousPage: page ? previousPages[page] : undefined,
        page,
        setPage: async (page, increment) => {
            await apiQueryClient.prefetchQuery(paginatedApiQuery(page));
            setState((state) => ({
                pageIndex: state.pageIndex + increment,
                page,
                previousPages:
                    increment > 0 && page
                        ? {
                              ...state.previousPages,
                              [page]: state.page,
                          }
                        : state.previousPages,
            }));
        },
    });
    return {
        ...controls,
        ...result,
        // True while showing previous data during a refetch triggered by a params/page change
        // (the non-suspense path keeps prior data via `placeholderData`, so `isLoading` stays false).
        isPlaceholderData: queryResult.isPlaceholderData,
        range:
            data?.count != null
                ? {
                      totalItems: data.count,
                      // Default limit is usually 10 items per page
                      itemsPerPage:
                          resource.query && 'limit' in resource.query && resource.query.limit
                              ? resource.query.limit
                              : 10,
                      page: state.pageIndex,
                  }
                : undefined,
        data,
    };
}

/**
 * Helper to paginate an API query.
 */
export function useSuspensePaginatedAPIQuery<
    TResource extends APIResourceInput,
    TSkip extends boolean = false,
>(
    resource: TResource,
    options?: {
        /** Skip the query. */
        skip?: TSkip;
        /**
         * Suspend while fetching data after parameters change.
         *
         * When false, the hook keeps returning the previous page as placeholder data
         * during refetch. This means returned data can be stale during that time,
         * which does not fit all use cases (e.g. filters, sorting...).
         *
         * Note: This does not suspend on pagination cursor changes, only on params changes.
         *
         * @default false
         */
        suspenseOnParamsChange?: boolean;
    }
) {
    return usePaginatedAPIQueryBase(resource, {
        suspense: true,
        skip: options?.skip ?? false,
        suspenseOnParamsChange: options?.suspenseOnParamsChange,
    });
}

/**
 * Helper to paginate an API query.
 */
export function usePaginatedAPIQuery<
    TResource extends APIResourceInput,
    TSkip extends boolean = false,
>(
    resource: TResource,
    options?: {
        /** Skip the query. */
        skip?: TSkip;
        /** Suspense the query. */
        suspense?: boolean;
    }
) {
    return usePaginatedAPIQueryBase(resource, {
        suspense: options?.suspense ?? false,
        skip: options?.skip ?? false,
    });
}

/**
 * Helper to accumulate every page of a paginated API query, for infinite scrolling.
 */
export function useInfiniteAPIQuery<TResource extends APIResourceInput>(resource: TResource) {
    type Data = api.OperationSpec<TResource['operationId']>['ResponseBody'] & api.List;
    type Item = Data extends { items: Array<infer TItem> } ? TItem : never;

    const resourceHash = hashKey(getAPIQueryKey(resource));
    const [pages, setPages] = useStateWithDeps<Array<string | null>>([null], [resourceHash], {
        resetOnDepsChange: true,
    });

    // `useQueries` cannot infer results from an array built at runtime, hence the annotation.
    const results: Array<UseQueryResult<Data, HTTPError>> = useQueries({
        queries: pages.map((page) =>
            apiQuery({
                ...resource,
                query:
                    resource.query || page
                        ? { ...resource.query, ...(page ? { page } : {}) }
                        : undefined,
            })
        ),
    });

    // Throw, like the paginated hooks: an error must not read as an empty list.
    const error = results.find((result) => result.error)?.error;
    if (error) {
        throw error;
    }

    const firstPage = results[0]?.data;
    const lastResult = results[results.length - 1];
    const nextPage = lastResult?.data?.next?.page ?? null;

    const fetchNextPage = useEventCallback(() => {
        if (!nextPage) {
            return;
        }
        setPages((pages) => (pages.includes(nextPage) ? pages : [...pages, nextPage]));
    });

    return {
        items: results.flatMap(
            (result) => (result.data as { items?: Item[] } | undefined)?.items ?? []
        ),
        // Every page carries the same unfiltered total, so the first one is enough.
        totalCount: firstPage?.count,
        isLoading: results[0]?.isPending ?? true,
        isFetchingNextPage: pages.length > 1 && Boolean(lastResult?.isPending),
        hasNextPage: Boolean(nextPage),
        fetchNextPage,
    };
}

const apiStreamAtom = atom<{
    [key: string]: { data: any[]; streaming: boolean; stream: AsyncIterable<any> } | undefined;
}>({});

const apiStreamSelector = atomFamily((key: string) =>
    atom(
        (get) => get(apiStreamAtom)[key],
        (
            get,
            set,
            update:
                | { data: any[]; streaming: boolean; stream: AsyncIterable<any> }
                | undefined
                | ((
                      previous:
                          | { data: any[]; streaming: boolean; stream: AsyncIterable<any> }
                          | undefined
                  ) => { data: any[]; streaming: boolean; stream: AsyncIterable<any> } | undefined)
        ) => {
            const previous = get(apiStreamAtom)[key];
            const next = typeof update === 'function' ? update(previous) : update;
            set(apiStreamAtom, (oldValue) => ({
                ...oldValue,
                [key]: next,
            }));
        }
    )
);

/**
 * Create a stable API resource input.
 */
function useStableAPIResourceInput<T extends APIResourceInput | null>(input: T): T {
    return useStable(input, (a, b) => {
        const hashA = a ? hashKey(getAPIQueryKey(a)) : null;
        const hashB = b ? hashKey(getAPIQueryKey(b)) : null;
        return hashA === hashB;
    });
}

/**
 * Hook to read an API stream.
 * Return an array of the messages received and a boolean indicating if the stream is active.
 */
export function useAPIStream<Id extends api.OperationId, R = api.OperationSpec<Id>['ResponseBody']>(
    input: APIResourceInput<Id> | null,
    options: {
        /**
         * Callback to be called when an error occurs.
         * If not provided, the hook will throw the error.
         */
        onError?: (error: Error) => void;

        /**
         * Optional. Function to transform the data received from the stream.
         */
        transform?: (data: api.OperationSpec<Id>['ResponseBody']) => Promise<R>;
    } = {}
): [R[], boolean] {
    const inputKey = input ? hashKey(getAPIQueryKey(input)) : '<null>';
    const stableInput = useStableAPIResourceInput(input);
    const [state, setState] = useAtom(apiStreamSelector(inputKey));
    const hasBeenStreamed = Boolean(state);
    const transform = useEventCallback(
        (options.transform ?? ((data) => data)) as (
            data: api.OperationSpec<Id>['ResponseBody']
        ) => Promise<R>
    );
    const handleError = useEventCallback(
        options.onError ??
            ((error) => {
                throw error;
            })
    );

    React.useEffect(() => {
        const input = stableInput;
        if (!input || hasBeenStreamed) {
            return;
        }

        const apiOperation = api.operations[input.operationId];
        if (!apiOperation.streaming) {
            throw new Error(`Operation ${input.operationId} is not a streaming operation`);
        }

        (async () => {
            const stream = apiClient.streamRequest<api.OperationSpec<Id>['ResponseBody'], any>({
                path: api.getAPIOperationPath(input),
                method: apiOperation.method,
                secure: true,
                format: 'json',
                query: input.query,
            });

            for await (const data of stream) {
                const result = await transform(data);
                setState((prev) => {
                    if (!prev) {
                        return {
                            data: [result],
                            streaming: true,
                            stream,
                        };
                    }

                    if (prev && prev.stream !== stream) {
                        // Started by another execution
                        return prev;
                    }

                    return {
                        data: [...prev.data, result],
                        streaming: true,
                        stream,
                    };
                });
            }

            setState((prev) => {
                if (!prev || prev.stream !== stream) {
                    return prev;
                }

                return {
                    ...prev,
                    streaming: false,
                };
            });
        })().catch(handleError);
    }, [stableInput, setState, hasBeenStreamed, handleError, transform]);

    return state ? [state.data, state.streaming] : [[], false];
}
